import React, { useEffect, useRef } from "react"
import "./AdventCalendar.scss"
import StarRedSvg from "@images/icon/star/icn-star-red.svg"
import h2V4AdventCalendarSvg from "@images/h2-v4-advent-calendar.svg"
import { StaticImage } from "gatsby-plugin-image"
import Rellax from "rellax"
import { Player } from "@lottiefiles/react-lottie-player"
import DecoLine01 from "@lotties/deco-line01.json"
import DecoLine02 from "@lotties/deco-line02.json"
import Division from "@lotties/division.json"
import DivisionShort from "@lotties/division-short.json"

const useLottieAnimation = delay => {
  const playerRef = useRef(null)

  useEffect(() => {
    const timeout = setTimeout(() => {
      playerRef.current?.play()
    }, delay)

    return () => clearTimeout(timeout)
  }, [delay])

  return playerRef
}

const AdventCalendar = ({ handleOpenModal, clickedDates, serverTime }) => {
  const isUnlocked = date => clickedDates.includes(date)

  const isDatePassed = date => {
    const currentDate = new Date(2024, 11, date)
    return serverTime && serverTime >= currentDate
  }

  // Lottie アニメーション
  const playerRef1 = useLottieAnimation(2000)
  const playerRef2 = useLottieAnimation(2000)

  // rellax アニメーション
  useEffect(() => {
    const rellax = new Rellax(".js-rellax", {
      center: true,
      breakpoints: [767],
    })
    return () => {
      rellax.destroy()
    }
  }, [])

  return (
    <section className="advent-calender" id="advent-calendar">
      <div className="advent-calendar-inner">
        <div className="advent-calendar-container">
          <h2 className="advent-calendar-title">
            <div className="advent-calendar-title-img">
              <img src={h2V4AdventCalendarSvg} alt="advent calendar title" />
            </div>
          </h2>
          <div className="star-red">
            <img src={StarRedSvg} alt="" />
          </div>
          <p className="advent-calendar-text">
            Discover the magic of the season with V4Mirai's Advent Calendar!
            Join us every day from December 1st to 25th for a new surprise! Each
            day unlock exclusive content,like beautifully crafted illustrations,
            captivating videos, and other delightful surprises designed to bring
            joy and holiday cheer! Whether you're here for your oshi, wanting
            fresh goodies , or simply a touch of holiday magic, our Advent
            Calendar has something special for everyone. Visit daily for
            something new, a bit of winter wonder from V4Mirai to you! Celebrate
            the season with us!
          </p>
          <div className="advent-calendar-items">
            {[...Array(25)].map((_, i) => {
              const date = (i + 1).toString()

              // 各ボタンのクラス名を設定
              const className = isUnlocked(date)
                ? `unlocked thumb-day${date}`
                : isDatePassed(date)
                ? `unlockable key-opened-day${date}`
                : `locked key-closed-day${date}`

              return (
                <React.Fragment key={date}>
                  {/* 24日目と25日目の間にスペーサーを挿入 */}
                  {i === 24 && <div className="spacer"></div>}
                  <button
                    className={`advent-calendar-item ${className}`}
                    onClick={() => isDatePassed(date) && handleOpenModal(date)}
                    data-gtm-click={`adventCalendar-${date}`}
                    disabled={!isDatePassed(date)}
                    aria-label="advent-calendar-item"
                  ></button>
                </React.Fragment>
              )
            })}
          </div>
        </div>
        <div className="advent-calendar-ornament">
          <div className="ornament-star">
            <StaticImage
              src="../../images/icon/advent-calendar/ornament-star.png"
              alt=""
            />
          </div>
          <div
            className="confetti confetti1 js-rellax"
            data-rellax-xs-speed="-0.1"
            data-rellax-speed="-0.5"
          >
            <StaticImage
              src="../../images/icon/advent-calendar/confetti.png"
              alt=""
            />
          </div>
          <div
            className="confetti confetti2 js-rellax"
            data-rellax-xs-speed="-0.1"
            data-rellax-speed="-0.5"
          >
            <StaticImage
              src="../../images/icon/advent-calendar/confetti.png"
              alt=""
            />
          </div>
          <div
            className="confetti confetti3 js-rellax"
            data-rellax-xs-speed="-0.1"
            data-rellax-speed="-0.5"
          >
            <StaticImage
              src="../../images/icon/advent-calendar/confetti.png"
              alt=""
            />
          </div>
          <div
            className="confetti-blur js-rellax"
            data-rellax-xs-speed="-1.1"
            data-rellax-speed="-1.5"
          >
            <StaticImage
              src="../../images/icon/advent-calendar/confetti-blur.png"
              alt=""
            />
          </div>
          <Player
            className="deco-line1 deco-line1-1"
            autoplay
            loop
            src={DecoLine01}
          />
          <Player
            className="deco-line2 deco-line2-1"
            autoplay
            loop
            src={DecoLine02}
          />
          <Player
            className="deco-line2 deco-line2-2"
            ref={playerRef1}
            loop
            src={DecoLine02}
          />
          <Player
            className="deco-line1 deco-line1-2"
            ref={playerRef2}
            loop
            src={DecoLine01}
          />
          <Player
            className="deco-line4 deco-line4-1"
            autoplay
            loop
            src={Division}
          />
          <Player
            className="deco-line4 deco-line4-2"
            autoplay
            loop
            src={Division}
          />
          <Player
            className="deco-line4-sp deco-line4-1"
            autoplay
            loop
            src={DivisionShort}
          />
          <Player
            className="deco-line4-sp deco-line4-2"
            autoplay
            loop
            src={DivisionShort}
          />
          <div
            className="ornament-small-sphere ornament-small-sphere1 js-rellax"
            data-rellax-xs-speed="-0.1"
            data-rellax-speed="-0.3"
          >
            <StaticImage
              src="../../images/icon/advent-calendar/ornament-small-sphere.png"
              alt=""
            />
          </div>
          <div
            className="ornament-small-sphere ornament-small-sphere2 js-rellax"
            data-rellax-xs-speed="-0.1"
            data-rellax-speed="-0.3"
          >
            <StaticImage
              src="../../images/icon/advent-calendar/ornament-small-sphere.png"
              alt=""
            />
          </div>
          <div
            className="ornament-small-sphere ornament-small-sphere3 js-rellax"
            data-rellax-xs-speed="-0.1"
            data-rellax-speed="-0.3"
          >
            <StaticImage
              src="../../images/icon/advent-calendar/ornament-small-sphere.png"
              alt=""
            />
          </div>
          <div
            className="ornament-sphere-img1 js-rellax"
            data-rellax-xs-speed="-0.2"
            data-rellax-speed="-0.7"
          >
            <StaticImage
              src="../../images/icon/advent-calendar/ornament-sphere1.png"
              alt=""
            />
          </div>
          <div
            className="ornament-sphere-img2 js-rellax"
            data-rellax-xs-speed="-0.2"
            data-rellax-speed="-0.7"
          >
            <StaticImage
              src="../../images/icon/advent-calendar/ornament-sphere2.png"
              alt=""
            />
          </div>
          <div
            className="ornament-sphere-img3 js-rellax"
            data-rellax-xs-speed="-0.8"
            data-rellax-speed="-1.2"
          >
            <StaticImage
              src="../../images/icon/advent-calendar/ornament-sphere3.png"
              alt=""
            />
          </div>
          <div
            className="ornament-sphere-img4 js-rellax"
            data-rellax-xs-speed="-0.2"
            data-rellax-speed="-0.7"
          >
            <StaticImage
              src="../../images/icon/advent-calendar/ornament-sphere4.png"
              alt=""
            />
          </div>
          <div
            className="ornament-sphere-img5 js-rellax"
            data-rellax-xs-speed="-0.2"
            data-rellax-speed="-1"
          >
            <StaticImage
              src="../../images/icon/advent-calendar/ornament-sphere5.png"
              alt=""
            />
          </div>
          <div
            className="ornament-sphere-img6 js-rellax"
            data-rellax-xs-speed="-0.2"
            data-rellax-speed="-0.7"
          >
            <StaticImage
              src="../../images/icon/advent-calendar/ornament-sphere6.png"
              alt=""
            />
          </div>
          <div
            className="ornament-sphere-img7 js-rellax"
            data-rellax-xs-speed="-0.2"
            data-rellax-speed="-0.7"
          >
            <StaticImage
              src="../../images/icon/advent-calendar/ornament-sphere7.png"
              alt=""
            />
          </div>
          <div
            className="ornament-sphere-img8 js-rellax"
            data-rellax-xs-speed="-0.2"
            data-rellax-speed="-0.7"
          >
            <StaticImage
              src="../../images/icon/advent-calendar/ornament-sphere8.png"
              alt=""
            />
          </div>
          <div
            className="ornament-sphere-img9 js-rellax"
            data-rellax-xs-speed="-0.4"
            data-rellax-speed="-1"
          >
            <StaticImage
              src="../../images/icon/advent-calendar/ornament-sphere9.png"
              alt=""
            />
          </div>
          <div
            className="ornament-sphere-img10 js-rellax"
            data-rellax-xs-speed="-0.3"
            data-rellax-speed="-0.8"
          >
            <StaticImage
              src="../../images/icon/advent-calendar/ornament-sphere10.png"
              alt=""
            />
          </div>
        </div>
      </div>
    </section>
  )
}

export default AdventCalendar
